/**
 * Éditeur enrichi léger (contenteditable) — conditions / pieds de page.
 * Pas d’image : le PDF les gérera plus tard.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import "./richText.css";

type RichTextProps = {
  label: string;
  value: string;
  onChange: (html: string) => void;
  resetKey?: string | number;
  disabled?: boolean;
  compact?: boolean;
  fonts?: boolean;
};

const FONTS = [
  { id: "Nunito, Poppins, sans-serif", label: "Nunito" },
  { id: "Poppins, sans-serif", label: "Poppins" },
  { id: "Georgia, serif", label: "Georgia" },
  { id: "Arial, sans-serif", label: "Arial" },
  { id: "Times New Roman, serif", label: "Times" },
];

const SIZES = ["12px", "14px", "16px", "18px", "20px", "24px"];

const COLORS = [
  "#111827",
  "#475569",
  "#dc2626",
  "#ea580c",
  "#ca8a04",
  "#16a34a",
  "#2563eb",
  "#4f46e5",
  "#7c3aed",
  "#db2777",
  "#ffffff",
];

function emptyHtml(html: string): boolean {
  return html.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").trim() === "";
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function inlineMd(s: string): string {
  return esc(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
}

/** Texte déjà saisi sans balises : paragraphes, et les marques ** ou ## deviennent gras et titres. */
export function richOrPlain(raw: string): string {
  const t = raw.trim();
  if (!t) return "";
  if (/<[a-z][^>]*>/i.test(t)) return t;
  return t
    .split(/\r?\n/)
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return "";
      const heading = /^(#{2,5})\s+(.+)$/.exec(trimmed);
      if (heading) {
        const level = heading[1].length;
        return `<h${level}>${inlineMd(heading[2])}</h${level}>`;
      }
      return `<p>${inlineMd(line)}</p>`;
    })
    .filter(Boolean)
    .join("");
}

export function RichText({ label, value, onChange, resetKey, disabled, compact, fonts = true }: RichTextProps) {
  const box = useRef<HTMLDivElement>(null);
  const own = useRef<string | null>(null);
  const [linkOn, setLinkOn] = useState(false);
  const [href, setHref] = useState("https://");
  const [palette, setPalette] = useState<"fg" | "bg" | null>(null);

  useEffect(() => {
    document.execCommand("defaultParagraphSeparator", false, "p");
    document.execCommand("styleWithCSS", false, "false");
  }, []);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const next = value || "";
    if (own.current === next) return;
    el.innerHTML = next;
    own.current = next;
  }, [value, resetKey]);

  function focus() {
    box.current?.focus();
  }

  function tidy(root: HTMLElement | null) {
    if (!root) return;
    root.querySelectorAll<HTMLElement>("span").forEach((span) => {
      if (!fonts) {
        span.style.fontSize = "";
        span.style.fontFamily = "";
      }
      span.style.fontWeight = "";
      if (!(span.getAttribute("style") ?? "").trim()) span.removeAttribute("style");
      if (span.attributes.length === 0) {
        const parent = span.parentNode;
        while (span.firstChild) parent?.insertBefore(span.firstChild, span);
        span.remove();
      }
    });
  }

  function emit() {
    tidy(box.current);
    const html = emptyHtml(box.current?.innerHTML ?? "") ? "" : (box.current?.innerHTML ?? "");
    own.current = html;
    onChange(html);
  }

  function currentBlock(): HTMLElement | null {
    const node = window.getSelection()?.anchorNode;
    const el = node instanceof Element ? node : node?.parentElement;
    const block = el?.closest("p, h2, h3, h4, h5, div, li");
    if (!block || !box.current?.contains(block) || block === box.current) return null;
    return block as HTMLElement;
  }

  function stripLook(el: HTMLElement) {
    el.querySelectorAll<HTMLElement>("span, font").forEach((span) => {
      span.style.fontWeight = "";
      span.style.fontSize = "";
      if (!(span.getAttribute("style") ?? "").trim()) span.removeAttribute("style");
      if (span.attributes.length === 0) {
        const parent = span.parentNode;
        while (span.firstChild) parent?.insertBefore(span.firstChild, span);
        span.remove();
      }
    });
  }

  function run(cmd: string, arg?: string) {
    focus();
    if (cmd === "formatBlock") {
      const tag = (arg || "p").replace(/[<>]/g, "");
      document.execCommand("styleWithCSS", false, "false");
      document.execCommand("formatBlock", false, `<${tag}>`);
      if (tag === "p") {
        const block = currentBlock();
        if (block) stripLook(block);
      }
      emit();
      return;
    }
    const paint = cmd === "foreColor" || cmd === "hiliteColor";
    document.execCommand("styleWithCSS", false, paint ? "true" : "false");
    document.execCommand(cmd, false, arg);
    if (paint) document.execCommand("styleWithCSS", false, "false");
    emit();
  }

  function fontSize(px: string) {
    focus();
    document.execCommand("styleWithCSS", false, "true");
    document.execCommand("fontSize", false, "7");
    box.current?.querySelectorAll('font[size="7"], span[style*="xxx-large"]').forEach((node) => {
      const el = node as HTMLElement;
      el.removeAttribute("size");
      el.style.fontSize = px;
    });
    document.execCommand("styleWithCSS", false, "false");
    emit();
  }

  function applyLink() {
    const url = href.trim();
    if (!/^https?:\/\//i.test(url) && !/^mailto:/i.test(url)) return;
    run("createLink", url);
    setLinkOn(false);
  }

  return (
    <div className={["fx-rich fx-field", compact ? "is-compact" : "", disabled ? "is-ro" : ""].filter(Boolean).join(" ")}>
      <span>{label}</span>
      {!disabled ? (
        <>
      <div className="fx-rich__bar">
        <select aria-label="Paragraphe" defaultValue="p" onChange={(e) => run("formatBlock", e.target.value)}>
          <option value="p">Paragraphe</option>
          <option value="h2">Titre 2</option>
          <option value="h3">Titre 3</option>
          <option value="h4">Titre 4</option>
          <option value="h5">Titre 5</option>
        </select>
        {fonts ? (
          <>
            <select aria-label="Police" defaultValue="" onChange={(e) => e.target.value && run("fontName", e.target.value)}>
              <option value="">Police</option>
              {FONTS.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
            <select aria-label="Taille" defaultValue="" onChange={(e) => e.target.value && fontSize(e.target.value)}>
              <option value="">Taille</option>
              {SIZES.map((s) => (
                <option key={s} value={s}>
                  {s.replace("px", "")}
                </option>
              ))}
            </select>
          </>
        ) : null}
        <IconBtn title="Gras" onClick={() => run("bold")}>
          <path d="M6 5h7a3.5 3.5 0 0 1 0 7H6zM6 12h8a3.5 3.5 0 0 1 0 7H6z" />
        </IconBtn>
        <IconBtn title="Italique" onClick={() => run("italic")}>
          <path d="M10 5h8M6 19h8M14 5l-4 14" />
        </IconBtn>
        <IconBtn title="Souligné" onClick={() => run("underline")}>
          <path d="M7 5v7a5 5 0 0 0 10 0V5M6 21h12" />
        </IconBtn>
        <IconBtn title="Barré" onClick={() => run("strikeThrough")}>
          <path d="M5 12h14M9 7c0-2 2-3 3.5-3S16 6 16 7.5M8 16.5C8 18.5 10 20 12.5 20S17 18 17 16" />
        </IconBtn>
        <IconBtn title="Couleur du texte" on={palette === "fg"} onClick={() => setPalette(palette === "fg" ? null : "fg")}>
          <path d="M5 19h14M9 16l3-10 3 10M10.2 13h3.6" />
        </IconBtn>
        <IconBtn title="Fond" on={palette === "bg"} onClick={() => setPalette(palette === "bg" ? null : "bg")}>
          <path d="M4 20h16M7 16l5-11 5 11" />
          <path d="M9 12h6" />
        </IconBtn>
        <IconBtn title="Puces" onClick={() => run("insertUnorderedList")}>
          <circle cx="6" cy="7" r="1.2" fill="currentColor" stroke="none" />
          <circle cx="6" cy="12" r="1.2" fill="currentColor" stroke="none" />
          <circle cx="6" cy="17" r="1.2" fill="currentColor" stroke="none" />
          <path d="M10 7h8M10 12h8M10 17h8" />
        </IconBtn>
        <IconBtn title="Numéros" onClick={() => run("insertOrderedList")}>
          <path d="M6 7h.01M6 12h.01M6 17h.01M10 7h8M10 12h8M10 17h8" />
        </IconBtn>
        <IconBtn title="Aligner à gauche" onClick={() => run("justifyLeft")}>
          <path d="M4 6h16M4 10h10M4 14h16M4 18h10" />
        </IconBtn>
        <IconBtn title="Centrer" onClick={() => run("justifyCenter")}>
          <path d="M4 6h16M7 10h10M4 14h16M7 18h10" />
        </IconBtn>
        <IconBtn title="Aligner à droite" onClick={() => run("justifyRight")}>
          <path d="M4 6h16M10 10h10M4 14h16M10 18h10" />
        </IconBtn>
        <IconBtn title="Justifier" onClick={() => run("justifyFull")}>
          <path d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </IconBtn>
        <IconBtn title="Lien" on={linkOn} onClick={() => setLinkOn((v) => !v)}>
          <path d="M10 13a5 5 0 0 0 7.1 0l1.4-1.4a5 5 0 0 0-7.1-7.1L10 6" />
          <path d="M14 11a5 5 0 0 0-7.1 0L5.5 12.4a5 5 0 0 0 7.1 7.1L14 18" />
        </IconBtn>
      </div>
      {palette ? (
        <div className="fx-rich__swatches" role="listbox" aria-label={palette === "fg" ? "Couleur du texte" : "Couleur de fond"}>
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              style={{ background: c }}
              aria-label={c}
              onClick={() => {
                run(palette === "fg" ? "foreColor" : "hiliteColor", c);
                setPalette(null);
              }}
            />
          ))}
        </div>
      ) : null}
      {linkOn ? (
        <div className="fx-rich__link">
          <input value={href} onChange={(e) => setHref(e.target.value)} placeholder="https://" aria-label="Adresse du lien" />
          <button type="button" onClick={applyLink}>
            OK
          </button>
        </div>
      ) : null}
        </>
      ) : null}
      <div
        ref={box}
        className="fx-rich__edit"
        contentEditable={!disabled}
        role="textbox"
        aria-label={label}
        aria-readonly={disabled || undefined}
        onInput={disabled ? undefined : emit}
        onBlur={disabled ? undefined : emit}
      />
    </div>
  );
}

function IconBtn({
  title,
  on,
  onClick,
  children,
}: {
  title: string;
  on?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" title={title} aria-label={title} className={on ? "is-on" : ""} onClick={onClick}>
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
        {children}
      </svg>
    </button>
  );
}
